import { useCallback, useEffect, useState } from "react";
import type {
  DeletionResult,
  ParentChildSummary,
  ParentDigest,
  ParentSessionResponse,
} from "@howcomet/shared";
import { AVATARS, PARENT_PASSWORD_MIN } from "@howcomet/shared";
import { api, ApiError } from "../api";
import { DigestView } from "./DigestView";

/**
 * The parent's own page, on its own URL, opened on their own device.
 *
 * The reason it isn't on the tablet: a passcode a child chose, on the machine
 * that child is holding, protects nothing. Moving the credential to an emailed
 * link is what makes the parent side actually the parent's — and it doubles as
 * the verifiable-consent channel COPPA wants, since the address is the one
 * thing the child can't control.
 */

const TOKEN_KEY = "howcomet.parent.token";

export function ParentPortal() {
  const [session, setSession] = useState<ParentSessionResponse | null>(null);
  const [activeChildId, setActiveChildId] = useState<string | null>(null);
  const [digest, setDigest] = useState<ParentDigest | null>(null);
  const [loading, setLoading] = useState(true);

  const setupToken = new URLSearchParams(window.location.search).get("token");
  const isSetupRoute = window.location.pathname.replace(/\/+$/, "").endsWith("/setup");

  useEffect(() => {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (!stored || (isSetupRoute && setupToken)) {
      setLoading(false);
      return;
    }
    api
      .parentSession(stored)
      .then((s) => {
        setSession(s);
        setActiveChildId(s.children[0]?.id ?? null);
      })
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setLoading(false));
  }, [isSetupRoute, setupToken]);

  const loadDigest = useCallback(() => {
    if (!session || !activeChildId) return;
    api
      .parentDigest(session.token, activeChildId)
      .then(setDigest)
      .catch(() => setDigest(null));
  }, [session, activeChildId]);

  useEffect(loadDigest, [loadDigest]);

  const signedIn = (s: ParentSessionResponse) => {
    localStorage.setItem(TOKEN_KEY, s.token);
    setSession(s);
    setActiveChildId(s.children[0]?.id ?? null);
    // Drop the one-time token out of the address bar so a shared screen or a
    // browser history entry can't be used to set the password a second time.
    if (isSetupRoute) window.history.replaceState({}, "", "/parents");
  };

  if (loading) {
    return (
      <Shell>
        <p className="text-cream/60">Loading…</p>
      </Shell>
    );
  }

  if (isSetupRoute && setupToken && !session) {
    return <SetupForm token={setupToken} onDone={signedIn} />;
  }

  if (!session) return <LoginForm onDone={signedIn} />;

  return (
    <Shell>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="disp text-[28px] text-cream">This week</p>
          <p className="text-[15px] text-cream/55">{session.email}</p>
        </div>
        <button
          onClick={() => {
            localStorage.removeItem(TOKEN_KEY);
            setSession(null);
            setDigest(null);
          }}
          className="kidbtn rounded-full bg-cream/12 px-4 py-2 text-[14px] font-bold text-cream"
        >
          Sign out
        </button>
      </div>

      {/*
        One email, several children. The tabs only appear when there's more than
        one — a single-child family should never see a switcher for a choice
        that doesn't exist.
      */}
      {session.children.length > 1 && (
        <div className="mb-6 flex flex-wrap gap-2">
          {session.children.map((child) => {
            const active = child.id === activeChildId;
            const avatar = AVATARS.find((a) => a.id === child.avatarId)?.emoji ?? "⭐";
            return (
              <button
                key={child.id}
                onClick={() => {
                  setActiveChildId(child.id);
                  setDigest(null);
                }}
                className={`kidbtn disp relative rounded-full px-5 py-3 text-[17px] ${
                  active ? "bg-gold text-night" : "bg-panel text-cream/75"
                }`}
              >
                <span className="mr-2">{avatar}</span>
                {child.name}
                {child.needsAttention && !active && (
                  <span className="absolute right-2.5 top-1.5 h-2.5 w-2.5 rounded-full bg-pink" />
                )}
              </button>
            );
          })}
        </div>
      )}

      {session.children.length === 0 ? (
        <div className="rounded-3xl bg-panel p-6">
          <p className="disp text-[20px] text-cream">No children yet</p>
          <p className="mt-2 text-cream/65">
            When a child sets up Howcomet with your email address, they'll appear here.
          </p>
        </div>
      ) : !digest ? (
        <p className="text-cream/60">Loading…</p>
      ) : (
        <DigestView token={session.token} digest={digest} onChanged={loadDigest} />
      )}

      {session.children.length > 0 && (
        <DeletePanel
          token={session.token}
          email={session.email}
          child={session.children.find((c) => c.id === activeChildId)}
          onGone={(accountGone) => {
            if (accountGone) {
              localStorage.removeItem(TOKEN_KEY);
              setSession(null);
              setDigest(null);
              return;
            }
            // One child went; the account did not. Reload the session so the
            // tabs and the digest stop referring to somebody who is not there.
            void api.parentSession(session.token).then(setSession).catch(() => setSession(null));
            setActiveChildId(null);
            setDigest(null);
          }}
        />
      )}
    </Shell>
  );
}

/**
 * Erasure, at the bottom of the page and behind two deliberate steps.
 *
 * Below everything else because it is the rarest thing a parent does and the
 * only one that cannot be taken back — but genuinely present, and reachable
 * without emailing anyone, because a parent's right to erase their child's data
 * is not something we grant on request.
 *
 * Nothing here is styled to be frightening. A parent choosing this has usually
 * decided already, and a wall of red does not make the decision better — it
 * just makes leaving feel like being told off.
 */
function DeletePanel({
  token,
  email,
  child,
  onGone,
}: {
  token: string;
  email: string;
  child: ParentChildSummary | undefined;
  onGone: (accountGone: boolean) => void;
}) {
  const [open, setOpen] = useState<"child" | "account" | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<DeletionResult | null>(null);

  const close = () => {
    setOpen(null);
    setPassword("");
    setConfirm("");
    setError("");
  };

  const submit = async () => {
    if (busy || !open) return;
    setBusy(true);
    setError("");
    try {
      const result =
        open === "account"
          ? await api.deleteAccount(token, password, confirm)
          : await api.deleteChild(token, child!.id, password, confirm);
      setDone(result);
      // Held for a beat so the parent reads what actually went, rather than
      // being dropped on the sign-in screen wondering whether it worked.
      setTimeout(() => onGone(result.accountGone), 4000);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "That didn't work.");
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="mt-10 rounded-3xl border border-cream/15 p-6">
        <p className="disp text-[18px] text-cream">Deleted</p>
        <p className="mt-2 text-[15px] text-cream/70">
          {done.deleted.stars} star{done.deleted.stars === 1 ? "" : "s"} and{" "}
          {done.deleted.questions} question{done.deleted.questions === 1 ? "" : "s"} have been
          removed. We've emailed you a confirmation.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-10 border-t border-cream/10 pt-6">
      <p className="disp mb-1 text-[15px] text-cream/70">Deleting data</p>
      <p className="mb-4 text-[14px] leading-relaxed text-cream/50">
        You can remove everything we hold about a child at any time. It is immediate, it cannot be
        undone, and we keep no copy.
      </p>

      {open === null && (
        <div className="flex flex-wrap gap-2">
          {child && (
            <button
              onClick={() => setOpen("child")}
              className="kidbtn rounded-full bg-cream/10 px-4 py-2 text-[14px] font-bold text-cream/85"
            >
              Delete {child.name}'s data
            </button>
          )}
          <button
            onClick={() => setOpen("account")}
            className="kidbtn rounded-full bg-cream/10 px-4 py-2 text-[14px] font-bold text-cream/85"
          >
            Delete my whole account
          </button>
        </div>
      )}

      {open !== null && (
        <div className="rounded-3xl bg-panel p-5">
          <p className="text-[15px] leading-relaxed text-cream">
            {open === "account"
              ? "This removes every child on this account, along with your own login."
              : `This removes ${child?.name}'s sky, stardust, questions and goals.`}{" "}
            It happens straight away and cannot be undone.
          </p>

          <label className="mt-4 block text-[13px] text-cream/60">
            Type your email address to confirm
            <input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={email}
              autoComplete="off"
              className="mt-1 w-full rounded-2xl bg-night px-4 py-3 text-[16px] text-cream outline-none"
            />
          </label>

          <label className="mt-3 block text-[13px] text-cream/60">
            And your password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="mt-1 w-full rounded-2xl bg-night px-4 py-3 text-[16px] text-cream outline-none"
            />
          </label>

          {error && <p className="mt-3 text-[14px] font-bold text-pink">{error}</p>}

          <div className="mt-4 flex gap-2">
            <button
              onClick={() => void submit()}
              disabled={busy || !password || !confirm}
              className="kidbtn rounded-full bg-pink px-5 py-3 text-[15px] font-extrabold text-night disabled:opacity-40"
            >
              {busy ? "Deleting…" : "Delete for good"}
            </button>
            <button
              onClick={close}
              className="kidbtn rounded-full bg-cream/10 px-5 py-3 text-[15px] font-bold text-cream"
            >
              Keep it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-dvh px-4 py-8">
      <div className="mx-auto max-w-2xl">
        <p className="disp mb-6 text-[15px] uppercase tracking-[0.15em] text-gold">
          ☄️ Howcomet · Grown-ups
        </p>
        {children}
      </div>
    </div>
  );
}

function SetupForm({
  token,
  onDone,
}: {
  token: string;
  onDone: (session: ParentSessionResponse) => void;
}) {
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    api
      .parentSetupCheck(token)
      .then((r) => setEmail(r.email))
      .catch((e) => setError(e instanceof ApiError ? e.message : "That link didn't work."))
      .finally(() => setChecking(false));
  }, [token]);

  const submit = async () => {
    if (busy) return;
    if (password !== confirm) {
      setError("Those two passwords don't match.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      onDone(await api.parentSetup(token, password));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't set that password.");
    } finally {
      setBusy(false);
    }
  };

  if (checking) {
    return (
      <Shell>
        <p className="text-cream/60">Checking your link…</p>
      </Shell>
    );
  }

  if (!email) {
    return (
      <Shell>
        <div className="rounded-3xl border-2 border-amber bg-panel p-6">
          <p className="disp text-[20px] text-amber">That link doesn't work</p>
          <p className="mt-2 leading-relaxed text-cream/75">{error}</p>
          <p className="mt-3 text-cream/65">
            Links expire, and they only work once. Use "I forgot my password" on the sign-in page to
            get a fresh one.
          </p>
          <a
            href="/parents"
            className="kidbtn disp mt-4 inline-block rounded-full bg-gold px-5 py-2 text-night"
          >
            Go to sign in
          </a>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <p className="disp text-[26px] text-cream">Make your password</p>
      <p className="mb-6 mt-1 leading-relaxed text-cream/65">
        For <b className="text-cream">{email}</b>. This is yours — don't give it to your child, or
        the parent side stops being the parent side.
      </p>

      <label className="mb-1 block text-[14px] uppercase tracking-wider text-cream/45">
        Password
      </label>
      <input
        type="password"
        autoComplete="new-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder={`At least ${PARENT_PASSWORD_MIN} characters`}
        className="w-full rounded-2xl bg-panel2 px-5 py-4 text-[17px] text-cream outline-none placeholder:text-cream/35"
      />

      <label className="mb-1 mt-4 block text-[14px] uppercase tracking-wider text-cream/45">
        Again
      </label>
      <input
        type="password"
        autoComplete="new-password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && void submit()}
        className="w-full rounded-2xl bg-panel2 px-5 py-4 text-[17px] text-cream outline-none"
      />

      {error && <p className="mt-3 text-[15px] text-amber">{error}</p>}

      <button
        onClick={() => void submit()}
        disabled={password.length < PARENT_PASSWORD_MIN || !confirm || busy}
        className="kidbtn disp mt-5 w-full rounded-3xl bg-gold py-4 text-[19px] text-night disabled:bg-cream/15 disabled:text-cream/40"
      >
        {busy ? "Saving…" : "Save and open my page"}
      </button>
    </Shell>
  );
}

function LoginForm({ onDone }: { onDone: (session: ParentSessionResponse) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sentReset, setSentReset] = useState(false);

  const submit = async () => {
    if (busy) return;
    setError("");
    setBusy(true);
    try {
      onDone(await api.parentLogin(email.trim().toLowerCase(), password));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't sign in.");
      setPassword("");
    } finally {
      setBusy(false);
    }
  };

  const forgot = async () => {
    if (!/.+@.+\..+/.test(email)) {
      setError("Type your email address first.");
      return;
    }
    setError("");
    await api.parentForgot(email.trim().toLowerCase()).catch(() => {});
    // Same message whether or not that address is known — anything else lets
    // someone probe which families use this.
    setSentReset(true);
  };

  return (
    <Shell>
      <p className="disp text-[26px] text-cream">Sign in</p>
      <p className="mb-4 mt-1 leading-relaxed text-cream/65">
        Use the email address your child gave when they set up Howcomet.
      </p>

      {/*
        A parent arriving here without a password has no way to tell whether
        they're forgetting one or never had one — and the page looked identical
        either way. Both roads lead to the same place, so say so rather than
        leaving them to guess which of the two they are.
      */}
      <p className="mb-6 rounded-2xl bg-panel p-4 text-[15px] leading-relaxed text-cream/60">
        <b className="text-cream">First time here?</b> Open the link in the email we sent you to
        make a password. Lost it, or not sure you ever made one? Use{" "}
        <b className="text-cream">I forgot my password</b> below — it works either way.
      </p>

      {sentReset ? (
        <div className="rounded-3xl bg-panel p-6">
          <p className="disp text-[20px] text-cream">Check your email</p>
          <p className="mt-2 leading-relaxed text-cream/70">
            If we know that address, there's a link on its way to set a new password.
          </p>
        </div>
      ) : (
        <>
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full rounded-2xl bg-panel2 px-5 py-4 text-[17px] text-cream outline-none placeholder:text-cream/35"
          />
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void submit()}
            placeholder="Password"
            className="mt-3 w-full rounded-2xl bg-panel2 px-5 py-4 text-[17px] text-cream outline-none placeholder:text-cream/35"
          />

          {error && <p className="mt-3 text-[15px] text-amber">{error}</p>}

          <button
            onClick={() => void submit()}
            disabled={!email || !password || busy}
            className="kidbtn disp mt-4 w-full rounded-3xl bg-gold py-4 text-[19px] text-night disabled:bg-cream/15 disabled:text-cream/40"
          >
            {busy ? "Checking…" : "Sign in"}
          </button>

          <button
            onClick={() => void forgot()}
            className="kidbtn mt-4 w-full py-2 text-[15px] text-cream/55"
          >
            I forgot my password
          </button>
        </>
      )}
    </Shell>
  );
}
